import React, { useEffect, useRef, useState } from 'react';
import {
    Button,
    TextField,
    Grid,
    Switch,
    FormControlLabel,
    Autocomplete,
    Box
} from '@mui/material';
import { Card } from "react-bootstrap";
import makeStyles from '@mui/styles/makeStyles';
import { Formik, FormikProps, FormikValues } from "formik";
import { Loadable, TextMaskCustom } from '../../../common';
import { DeleteConfirmation } from '../../../common/GenericDeleteConfirmationDialog/DeleteConfirmationDialog';
import { getProviderCCN } from '../../../store/api/facilities';
import { useData } from '@pure-ptr/react';
import BillingProviderNPIFieldArray from '../FacilityDialog/BillingProviderNPIFieldArray';
import { getClientAddress } from '../../../store/api/clients';
import { Portlet, PortletBody, PortletHeader, PortletHeaderToolbar } from '../../../partials/content/Portlet';
import { Helmet } from "react-helmet";
import { BackButton } from '../../../common/BackButton';
import { ImportNPIDialog } from './ImportNPIDialog/index';

const useStyles = makeStyles(theme => ({
    textField: {
        width: '100%',
    },
    root: {
        flexGrow: 1,
    },
    container: {
        display: 'flex',
        flexWrap: 'wrap',
    },
    formControl: {
        marginTop: theme.spacing(2),
        width: '100%',
    },
}));

export default function FacilityEditPage(props) {
    const {
        mode, intl, initialValues, facilityCreatePending,
        facilityUpdatePending, currentClient, getFormattedState, STATES, preferredDashboardClient,
        facilityId, facility, facilityGetPending, history,
        actions: { facilityCreate, facilityUpdate, facilityGet }
    } = props;

    const [findPoviderCCNIndex, setFindPoviderCCNIndex] = useState(-1);
    const [openImportDialog, setOpenImportDialog] = useState(false);
    const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
    const formikRef = useRef<FormikProps<FormikValues>>(null);
    const classes = useStyles();

    useEffect(() => {
        if (mode === 'edit' && !facility) {
            facilityGet(facilityId);
        }
    }, [mode, facilityId, facility, facilityGet]);

    const { data, isPending } = useData(async () => {
        if (findPoviderCCNIndex >= 0) {
            setFindPoviderCCNIndex(-1);
            const values = formikRef.current?.values;
            if (values?.billingProviderNpiRequest[findPoviderCCNIndex]?.billingProviderNPI) {
                let payload = {
                    billingProviderNPIs: [values?.billingProviderNpiRequest[findPoviderCCNIndex]?.billingProviderNPI],
                    facilityNPI: values?.billingProviderNpiRequest[findPoviderCCNIndex]?.billingProviderNPI
                }
                const result = await getProviderCCN(payload);
                formikRef.current?.setFieldValue(`billingProviderNpiRequest.${findPoviderCCNIndex}.providerCCN`, result);
                return result;
            }
            return null;
        }
    }, [findPoviderCCNIndex]);

    function handleCallback(reload) {
        if (reload) {
            history.push('/cms/facilities');
        }
    }

    const handleImportNPIs = (selectedNpis) => {
        const currentNpis = formikRef.current?.values.billingProviderNpiRequest || [];
        
        // Filter out empty initial record if it's the only one
        const initialNpis = (currentNpis.length === 1 && currentNpis[0].billingProviderNPI === '' && currentNpis[0].providerCCN === '')
            ? []
            : currentNpis;

        const newNpis = [...initialNpis];
        
        selectedNpis.forEach(imported => {
            // Check if already exists to avoid duplicates
            const exists = newNpis.some(npi => 
                npi.billingProviderNPI === imported.billingProviderNPI && 
                npi.providerCCN === imported.providerCCN
            );
            if (!exists) {
                newNpis.push({
                    billingProviderNPI: imported.billingProviderNPI,
                    providerCCN: imported.providerCCN || ''
                });
            }
        });

        formikRef.current?.setFieldValue('billingProviderNpiRequest', newNpis);
    };

    return (
        <Loadable loading={facilityGetPending}>
            <Helmet>
                <title>{mode === 'edit' ? 'Edit Facility' : 'Add Facility'}</title>
            </Helmet>
            <Formik
                innerRef={formikRef}
                enableReinitialize={true}
                initialValues={initialValues}
                validate={values => {
                    const errors: any = {};
                    const npiRegex = /^\d{10}$/;
                    const zipRegex = /^\d{5}(-\d{4})?$/;
                    const ccnRegex = /^[A-Z0-9]{6,13}$/;
                    if (!values.name) {
                        errors.name = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }
                    if (!values.address) {
                        errors.address = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (values?.address?.length > 500) {
                        errors.address = "Address should not exceed 500 characters.";
                    }

                    if (values?.streetAddress?.length > 500) {
                        errors.address = "Street Address should not exceed 500 characters.";
                    }

                    if (!values?.state) {
                        errors.state = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD",
                        });
                    }

                    if (!values?.zip) {
                        errors.zip = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD",
                        });
                    }
                    if (values?.zip && !zipRegex.test(values.zip)) {
                        errors.zip = 'Invalid ZIP code format';
                    }

                    if (!values?.city) {
                        errors.city = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD",
                        });
                    }

                    if (values.phone.replace(/\D/g, '').length !== 0 && !(/^[1-9]\d{9}$/.test(values.phone.replace(/\D/g, '')))) {
                        errors.phone = 'Invalid Phone number';
                    }

                    if (values?.facilityNPI && !npiRegex.test(values?.facilityNPI)) {
                        errors.facilityNPI = "Facility NPI Must be a 10 Digit Number";
                    }

                    if (!values.legalName?.trim()) {
                        errors.legalName = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                    }

                    if (values.legalName && values.legalName.length > 500) {
                        errors.legalName = "Legal Name should not exceed 500 characters.";
                    }

                    const billingProviderNpiRequestError = values.billingProviderNpiRequest.map((npiRecord, index) => {
                        const npiRecordError = {};
                        let hasError = false;
                        if ((npiRecord.billingProviderNPI !== null && npiRecord.billingProviderNPI !== "") || (npiRecord.providerCCN !== null && npiRecord.providerCCN !== "")) {
                            if (npiRecord.billingProviderNPI === '') {
                                npiRecordError['billingProviderNPI'] = intl.formatMessage({
                                    id: "AUTH.VALIDATION.REQUIRED_FIELD"
                                });
                                hasError = true;
                            } else if (npiRecord.billingProviderNPI && !npiRegex.test(npiRecord.billingProviderNPI)) {
                                npiRecordError['billingProviderNPI'] = 'NPI must be a 10 Digit Number.'
                                hasError = true;
                            }

                            if (npiRecord.providerCCN && !ccnRegex.test(npiRecord.providerCCN)) {
                                npiRecordError['providerCCN'] = 'Provider CCN must be 6 to 13 alphanumeric characters.'
                                hasError = true;
                            }
                        }

                        if (hasError) {
                            return npiRecordError;
                        }

                        return false;
                    });

                    const seenKeys = new Map();
                    values.billingProviderNpiRequest.forEach((record, index) => {
                        const key = `${record.billingProviderNPI?.trim() || ''}__${record.providerCCN?.trim() || ''}`;
                        if (key !== '__') {
                            if (seenKeys.has(key)) {
                                const duplicateError = billingProviderNpiRequestError[index] || {};
                                duplicateError['billingProviderNPI'] = "Duplicate record.";
                                billingProviderNpiRequestError[index] = duplicateError;
                            } else {
                                seenKeys.set(key, index);
                            }
                        }
                    });

                    const filteredBillingProviderNpiRequestErr = billingProviderNpiRequestError.filter(value => Object.keys(value).length > 0);
                    const hasAtLeastOneRecord = values.billingProviderNpiRequest.some(record =>
                        (record.billingProviderNPI && record.billingProviderNPI.trim() !== '') ||
                        (record.providerCCN && record.providerCCN.trim() !== '')
                    );

                    if (filteredBillingProviderNpiRequestErr.length > 0) {
                        errors['billingProviderNpiRequest'] = billingProviderNpiRequestError;
                    }
                    else if (!hasAtLeastOneRecord) {
                        const oneRecordError = {};
                        oneRecordError['billingProviderNPI'] = intl.formatMessage({
                            id: "AUTH.VALIDATION.REQUIRED_FIELD"
                        });
                        billingProviderNpiRequestError[0] = oneRecordError;
                        errors['billingProviderNpiRequest'] = billingProviderNpiRequestError;
                    }
                    return errors;
                }}
                onSubmit={(values) => {
                    let payload = {
                        city: values.city?.trim(),
                        state: typeof values.state === 'object' && values.state !== null ? values.state.displayName : values.state,
                        zip: values.zip,
                        streetAddress: values.streetAddress?.trim(),
                        id: undefined,
                        name: values.name,
                        legalName: values.legalName?.trim(),
                        description: values.description,
                        ...(values.ein?.trim() && values.ein.trim() !== '-' ? { ein: values.ein.trim() } : {}),
                        ...(values.tin?.trim() && values.tin.trim() !== '-' ? { tin: values.tin.trim() } : {}),
                        address: values.address?.trim(),
                        phone: values.phone.replace(/\D/g, '').length === 0 ? '' : values.phone,
                        active: values.active,
                        enableForPricer: values.enableForPricer,
                        facilityNPI: values.facilityNPI,
                        billingProviderNpiRequest: values.billingProviderNpiRequest
                            .filter(npiRecord => (npiRecord.billingProviderNPI !== null && npiRecord.billingProviderNPI !== "") || (npiRecord.providerCCN !== null && npiRecord.providerCCN !== ""))
                            .map(item => ({
                                billingProviderNPI: item.billingProviderNPI,
                                providerCCN: item.providerCCN,
                            })) || [],
                    }
                    if (mode === 'create') {
                        facilityCreate(payload, handleCallback, true);
                    }
                    else {
                        payload = {
                            ...payload,
                            id: values.id,

                        }
                        facilityUpdate(payload, handleCallback, true);
                    }
                }}
            >
                {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    setFieldValue,
                    dirty,
                }) => {

                    const { data: clientAddressDetails, isPending: isFetchingClientDetails } = useData(async () => {
                        if (!currentClient && !preferredDashboardClient) return;
                        if (!values?.applyClientAddress) {
                            handleToggleChange(values?.applyClientAddress, null)
                            return
                        }
                        const result = await getClientAddress(currentClient || preferredDashboardClient);

                        handleToggleChange(values?.applyClientAddress, result)
                        return result
                    }, [currentClient, values?.applyClientAddress]);

                    const handleToggleChange = (applyClientAddress, details) => {
                        const clientInfo = applyClientAddress ? details : mode === 'edit' ? { ...initialValues, state: initialValues?.state?.abbreviation } : {};

                        setFieldValue("address", clientInfo?.address || "");
                        setFieldValue("streetAddress", clientInfo?.streetAddress || "");
                        setFieldValue("city", clientInfo?.city || "");
                        setFieldValue("zip", clientInfo?.zip || "");
                        setFieldValue(
                            "state",
                            (applyClientAddress || (!applyClientAddress && mode === 'edit'))
                                ? getFormattedState(clientInfo?.state, STATES)?.displayName || ""
                                : ""
                        );
                    };

                    return (
                        <form onSubmit={handleSubmit} noValidate autoComplete="off" className={classes.container}>
                            <Portlet>
                                <PortletHeader
                                    toolbar={
                                        <PortletHeaderToolbar className='w-100'>
                                            <Grid container alignItems="center">
                                                <Grid xs={12} md={12} container className="justify-content-end">
                                                    {dirty ? (
                                                        <Button
                                                            variant="contained"
                                                            color="inherit"
                                                            onClick={() => setShowLeaveConfirmation(true)}
                                                            className='mr-2'
                                                        >
                                                            Cancel
                                                        </Button>
                                                    ) : (
                                                        <BackButton text="Back" defaultRoute="/cms/facilities" />
                                                    )}
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        className='ml-3'
                                                        type="submit"
                                                        disabled={facilityCreatePending || facilityUpdatePending || isPending || !dirty}
                                                    >
                                                        {(facilityCreatePending || facilityUpdatePending) ? 'Saving..' : 'Save'}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </PortletHeaderToolbar>
                                    }
                                />
                                <PortletBody>
                                    <Loadable loading={facilityCreatePending || facilityUpdatePending || !!isPending || !!isFetchingClientDetails}>
                                        <div className={classes.root}>
                                            <Grid container spacing={3}>
                                                <Grid item xs={12} md={9}>
                                                    <Grid container spacing={3}>
                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                required
                                                                label="Name"
                                                                name="name"
                                                                variant="standard"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values.name}
                                                                helperText={touched.name && errors.name}
                                                                error={Boolean(touched.name && errors.name)}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                required
                                                                label="Legal Name"
                                                                name="legalName"
                                                                variant="standard"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values.legalName}
                                                                helperText={touched.legalName && errors.legalName}
                                                                error={Boolean(touched.legalName && errors.legalName)}
                                                                inputProps={{ maxLength: 500 }}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                label="EIN"
                                                                name="ein"
                                                                variant="standard"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values.ein}
                                                                helperText={touched.ein && errors.ein}
                                                                error={Boolean(touched.ein && errors.ein)}
                                                                inputProps={{
                                                                    mask: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
                                                                }}
                                                                InputProps={{
                                                                    inputComponent: TextMaskCustom,
                                                                }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12}>
                                                            <TextField
                                                                label="Description"
                                                                name="description"
                                                                variant="standard"
                                                                multiline
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values.description}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12}>
                                                            <FormControlLabel 
                                                                control={<Switch checked={values?.applyClientAddress} onChange={(e) => setFieldValue('applyClientAddress', e.target.checked)} />} 
                                                                label="Apply Client Address" 
                                                                sx={{ mt: 1, mb: 1 }} 
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                required
                                                                variant="standard"
                                                                label="Street Address Line 1"
                                                                name="address"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={(e) => {
                                                                    if (e.target.value.length < 251) {
                                                                        handleChange(e)
                                                                    }
                                                                }}
                                                                value={values.address}
                                                                helperText={touched.address && errors.address}
                                                                error={Boolean(touched.address && errors.address)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <TextField
                                                                variant="standard"
                                                                label="Street Address Line 2 (Optional)"
                                                                name="streetAddress"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={(e) => {
                                                                    if (e.target.value.length < 251) {
                                                                        handleChange(e)
                                                                    }
                                                                }}
                                                                value={values.streetAddress}
                                                                helperText={touched.streetAddress && errors.streetAddress}
                                                                error={Boolean(touched.streetAddress && errors.streetAddress)}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={4}>
                                                            <TextField
                                                                required
                                                                variant="standard"
                                                                label="City"
                                                                name="city"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={(e) => {
                                                                    if (e.target.value.length < 101) {
                                                                        handleChange(e)
                                                                    }
                                                                }}
                                                                value={values?.city || ''}
                                                                helperText={touched.city && errors.city}
                                                                error={Boolean(touched.city && errors.city)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <Autocomplete
                                                                value={values.state || null}
                                                                disableClearable={false}
                                                                onChange={(e, value) => {
                                                                    setFieldValue("state", value?.displayName || null)
                                                                }}
                                                                isOptionEqualToValue={(option, value) => option?.displayName == value}
                                                                options={STATES}
                                                                getOptionLabel={(option) => option?.displayName || option}
                                                                renderInput={(params) =>
                                                                    <TextField {...params}
                                                                        variant="standard"
                                                                        name={`state`}
                                                                        label="State*"
                                                                        error={Boolean(touched.state && errors.state)}
                                                                        helperText={touched.state && errors.state}
                                                                    />
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <TextField
                                                                required
                                                                variant="standard"
                                                                label="Zip"
                                                                name="zip"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values?.zip || ''}
                                                                helperText={touched.zip && errors.zip}
                                                                error={Boolean(touched.zip && errors.zip)}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={4}>
                                                            <TextField
                                                                label="Phone"
                                                                name="phone"
                                                                variant="standard"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values.phone}
                                                                helperText={touched.phone && errors.phone}
                                                                error={Boolean(touched.phone && errors.phone)}
                                                                InputProps={{
                                                                    inputComponent: TextMaskCustom,
                                                                }}
                                                                InputLabelProps={{ shrink: true }}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <TextField
                                                                type="number"
                                                                variant="standard"
                                                                label="Facility NPI"
                                                                name="facilityNPI"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values.facilityNPI}
                                                                helperText={touched.facilityNPI && errors.facilityNPI}
                                                                error={Boolean(touched.facilityNPI && errors.facilityNPI)}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={4}>
                                                            <TextField
                                                                label="TIN"
                                                                name="tin"
                                                                variant="standard"
                                                                className={classes.textField}
                                                                onBlur={handleBlur}
                                                                onChange={handleChange}
                                                                value={values.tin}
                                                                helperText={touched.tin && errors.tin}
                                                                error={Boolean(touched.tin && errors.tin)}
                                                                inputProps={{
                                                                    mask: [/\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]
                                                                }}
                                                                InputProps={{
                                                                    inputComponent: TextMaskCustom,
                                                                }}
                                                            />
                                                        </Grid>

                                                        <Grid item xs={12} md={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        name="active"
                                                                        color="primary"
                                                                        checked={values.active}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                }
                                                                label="Enable facility"
                                                            />
                                                        </Grid>
                                                        <Grid item xs={12} md={6}>
                                                            <FormControlLabel
                                                                control={
                                                                    <Switch
                                                                        name="enableForPricer"
                                                                        color="primary"
                                                                        checked={values.enableForPricer}
                                                                        onChange={handleChange}
                                                                        onBlur={handleBlur}
                                                                    />
                                                                }
                                                                label="Enable Pricer"
                                                            />
                                                        </Grid>

                                                        <Grid id="billingNPI-div" item xs={12} className="mt-4">
                                                            <Card className="border-0 shadow-sm">
                                                                <Card.Header as="h5" className="bg-light d-flex justify-content-between align-items-center">
                                                                    <span>Billing Provider NPI Configurations *</span>
                                                                    <Button 
                                                                        variant="contained" 
                                                                        color="primary" 
                                                                        size="small"
                                                                        onClick={() => setOpenImportDialog(true)}
                                                                        sx={{ textTransform: 'none' }}
                                                                    >
                                                                        IMPORT NPIs
                                                                    </Button>
                                                                </Card.Header>
                                                                <Card.Body>
                                                                    <BillingProviderNPIFieldArray
                                                                        values={values}
                                                                        errors={errors}
                                                                        setFieldValue={setFieldValue}
                                                                        disabled={false}
                                                                        setFindPoviderCCNIndex={setFindPoviderCCNIndex}
                                                                    />
                                                                </Card.Body>
                                                            </Card>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </div>
                                    </Loadable>
                                </PortletBody>
                            </Portlet>
                        </form>
                    )
                }}
            </Formik>
            <ImportNPIDialog 
                open={openImportDialog} 
                onClose={() => setOpenImportDialog(false)} 
                onImport={handleImportNPIs}
                existingNpis={formikRef.current?.values.billingProviderNpiRequest || []}
            />
            <DeleteConfirmation
                defaultDeleteState={{ open: showLeaveConfirmation, rowData: null }}
                handleClose={() => setShowLeaveConfirmation(false)}
                handleDelete={() => history.push('/cms/facilities')}
                isConfirmationOnly={true}
                customTitle="Unsaved Changes"
                DELETE_TEXT="You have unsaved changes. Are you sure you want to leave without saving?"
                customConfirmBtnText="Leave"
                defaultSelected={[]}
            />
        </Loadable>
    );
}
